import { useCallback, useEffect, useState } from "react";
import type { World } from "@cimara/world";
import {
  apiErrorMessage,
  ApiError,
  type Me,
  type RaceApi,
  type RaceRegistration,
  type RaceSummary,
} from "../api";
import { isProfileReadyForRace } from "../auth/profile";
import {
  clearCachedRegistration,
  readCachedRegistration,
  saveCachedRegistration,
} from "../auth/registrationCache";
import {
  canRegisterNow,
  categoryRule,
  formatStartAt,
  formatStartIn,
  humanCapacity,
  phaseLabel,
} from "../race/format";
import { AppHeader } from "./AppHeader";
import { CourseMap } from "./CourseMap";

interface RaceDetailScreenProps {
  api: RaceApi;
  raceId: string;
  me: Me | null;
  world: World | null;
  reason: string | null;
  onSignIn(): void;
  onProfile(): void;
  onBack(): void;
  onHome(): void;
  onHistory(): void;
  onSignOut(): void;
  onEnterRace(race: RaceSummary, registration: RaceRegistration | null): void;
  onResults(raceId: string): void;
}

export function RaceDetailScreen({
  api,
  raceId,
  me,
  world,
  reason,
  onSignIn,
  onProfile,
  onBack,
  onHome,
  onHistory,
  onSignOut,
  onEnterRace,
  onResults,
}: RaceDetailScreenProps) {
  const [race, setRace] = useState<RaceSummary | null>(null);
  const [registration, setRegistration] = useState<RaceRegistration | null>(() =>
    readCachedRegistration(me?.user.id, raceId),
  );
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const next = await api.getRace(raceId, signal);
        if (signal?.aborted) return;
        setRace(next);
        if (next.registration) {
          setRegistration(next.registration);
          saveCachedRegistration(me?.user.id, next.registration);
        }
        if (next.phase === "finished") {
          setRegistration(null);
          setAlreadyRegistered(false);
          clearCachedRegistration(me?.user.id, raceId);
        }
        if (next.isRegistered) setAlreadyRegistered(true);
        setError(null);
      } catch (cause) {
        if (!signal?.aborted) setError(apiErrorMessage(cause));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [api, me?.user.id, raceId],
  );

  useEffect(() => {
    setRegistration(readCachedRegistration(me?.user.id, raceId));
    setAlreadyRegistered(false);
    setActionError(null);
  }, [me?.user.id, raceId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    const timer = window.setInterval(() => void load(), 15_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load]);

  useEffect(() => {
    if (!race || selectedCategory) return;
    const preferred = me?.profile?.category;
    setSelectedCategory(
      preferred && race.categories.includes(preferred)
        ? preferred
        : (race.categories[0] ?? "D"),
    );
  }, [me?.profile?.category, race, selectedCategory]);

  const isRegistered =
    registration !== null || alreadyRegistered || race?.isRegistered === true;
  const profileReady = isProfileReadyForRace(me);
  const humanSlots = race
    ? humanCapacity(race.capacity, race.botCount) || race.capacity
    : 0;
  const open = race
    ? canRegisterNow(race.phase, race.registrationOpen, race.registered, humanSlots)
    : false;
  const full = humanSlots > 0 && (race?.registered ?? 0) >= humanSlots;
  const canRegister = Boolean(
    me && profileReady && !isRegistered && open && selectedCategory,
  );

  const register = async (): Promise<void> => {
    if (!race || !selectedCategory) return;
    setRegistering(true);
    setActionError(null);
    try {
      const next = await api.registerForRace(race.id, selectedCategory as "A" | "B" | "C" | "D");
      setRegistration(next);
      setAlreadyRegistered(false);
      saveCachedRegistration(me?.user.id, next);
      await load();
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        onSignIn();
        return;
      }
      if (cause instanceof ApiError && cause.code === "already_registered") {
        setAlreadyRegistered(true);
        setActionError(null);
        return;
      }
      setActionError(apiErrorMessage(cause));
    } finally {
      setRegistering(false);
    }
  };

  const withdraw = async (): Promise<void> => {
    if (!race) return;
    setWithdrawing(true);
    setActionError(null);
    try {
      await api.withdrawFromRace(race.id);
      setRegistration(null);
      setAlreadyRegistered(false);
      clearCachedRegistration(me?.user.id, race.id);
      await load();
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        onSignIn();
        return;
      }
      setActionError(apiErrorMessage(cause));
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <main className="page-screen race-detail-screen">
      <AppHeader
        me={me}
        onHome={onHome}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        onProfile={onProfile}
        onHistory={onHistory}
        compact
      />

      <div className="race-detail__back">
        <button type="button" className="text-button" onClick={onBack}>
          ← Back to calendar
        </button>
      </div>

      {loading && !race ? (
        <div className="page-loading" role="status">
          Reading the race sheet…
        </div>
      ) : error && !race ? (
        <div className="load-error" role="alert">
          <p>{error}</p>
          <button type="button" className="text-button" onClick={() => void load()}>
            Try again
          </button>
        </div>
      ) : race ? (
        <div className="race-detail">
          <section className="race-detail__main">
            <div className="race-detail__head">
              <div>
                <p className="eyebrow">
                  <span className="eyebrow__line" />
                  Race detail
                </p>
                <h1 className="race-detail__title">{race.name}</h1>
                <p className="race-detail__route">
                  {race.routeName} · {race.laps} {race.laps === 1 ? "lap" : "laps"}
                  {race.distanceM ? ` · ${(race.distanceM / 1000).toFixed(1)} km` : ""}
                </p>
              </div>
              <span className={`phase-chip phase-chip--${race.phase}`}>
                {phaseLabel(race.phase)}
              </span>
            </div>

            {reason ? <p className="auth-reason">{reason}</p> : null}
            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}

            <dl className="detail-stats">
              <div>
                <dt>Starts</dt>
                <dd>{formatStartAt(race.startAt)}</dd>
                <span>{formatStartIn(race.startAt)}</span>
              </div>
              <div>
                <dt>Route</dt>
                <dd>{race.routeName}</dd>
                <span>Route {race.routeId}</span>
              </div>
              <div>
                <dt>Laps</dt>
                <dd>{race.laps}</dd>
                <span>{race.distanceM ? `${(race.distanceM / 1000).toFixed(1)} km` : "Distance TBD"}</span>
              </div>
              <div>
                <dt>Field</dt>
                <dd>
                  {race.registered} / {humanSlots}
                </dd>
                <span>
                  {full
                    ? "Race full"
                    : race.registrationOpen
                      ? "Registration open"
                      : phaseLabel(race.phase)}
                </span>
              </div>
            </dl>

            <div className="detail-section">
              <div className="section-head section-head--tight">
                <div>
                  <p className="panel-kicker">Categories</p>
                  <h2>Choose your race</h2>
                </div>
              </div>
              <div className="category-list">
                {race.categories.map((category) => (
                  <div className="category-row" key={category}>
                    <span className="category-badge">{category}</span>
                    <span>{categoryRule(category)}</span>
                  </div>
                ))}
                {race.categories.length === 0 ? <p>Open to all categories.</p> : null}
              </div>
              <p className="detail-note">
                Categories are enforced at registration. You may race up but not down.
                Riders self-select for their first three races; the server recalculates
                from 20-minute power after that.
              </p>
            </div>

            {race.rules.length > 0 ? (
              <div className="detail-section">
                <p className="panel-kicker">Rules</p>
                <ul className="detail-rules">
                  {race.rules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          <aside className="race-detail__side">
            <div className="race-detail__map">
              <CourseMap world={world} routeId={race.routeId} label={race.routeName} />
            </div>

            <div className="register-panel">
              <div className="register-panel__head">
                <div>
                  <p className="panel-kicker">Entry</p>
                  <h2>
                    {race.phase === "finished"
                      ? "Finished"
                      : isRegistered
                        ? "You are in"
                        : "Register"}
                  </h2>
                </div>
                <span className={`phase-chip phase-chip--${race.phase}`}>
                  {phaseLabel(race.phase)}
                </span>
              </div>

              {race.phase === "finished" ? (
                <div className="register-guest">
                  <p>This race has finished. Results are final.</p>
                  <button
                    type="button"
                    className="ride-button ride-button--compact"
                    onClick={() => onResults(race.id)}
                  >
                    View results
                  </button>
                </div>
              ) : !me ? (
                <div className="register-guest">
                  <p>Guest riders can free ride, but races require an account.</p>
                  <button type="button" className="ride-button ride-button--compact" onClick={onSignIn}>
                    Sign in to race
                  </button>
                </div>
              ) : isRegistered ? (
                <div className="registration-state" role="status">
                  <p className="registration-state__line">
                    Registered
                    {registration
                      ? ` · Category ${registration.category} · Pen ${registration.pen}`
                      : " · waiting for pen assignment"}
                  </p>
                  <button
                    type="button"
                    className="ride-button ride-button--compact"
                    data-testid="enter-race"
                    onClick={() => onEnterRace(race, registration)}
                  >
                    {race.phase === "pen" || race.phase === "countdown" || race.phase === "racing"
                      ? "Enter race"
                      : "Warm up in the world"}
                  </button>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => void withdraw()}
                    disabled={withdrawing}
                  >
                    {withdrawing ? "Withdrawing…" : "Withdraw"}
                  </button>
                  {!registration ? (
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => void load()}
                    >
                      Refresh pen details
                    </button>
                  ) : null}
                </div>
              ) : !profileReady ? (
                <div className="register-guest">
                  <p>
                    Add a display name, weight, and height before your first race
                    registration.
                  </p>
                  <button type="button" className="ride-button ride-button--compact" onClick={onProfile}>
                    Complete profile
                  </button>
                </div>
              ) : (
                <div className="register-form">
                  <label className="field">
                    <span className="field__label">Category</span>
                    <select
                      className="field__input"
                      value={selectedCategory}
                      onChange={(event) => setSelectedCategory(event.target.value)}
                      disabled={!canRegister}
                    >
                      {race.categories.map((category) => (
                        <option key={category} value={category}>
                          {category} — {categoryRule(category)}
                        </option>
                      ))}
                    </select>
                    <span className="field__hint">
                      {me.profile?.racesCompleted !== undefined &&
                      me.profile.racesCompleted < 3
                        ? "Self-select is open for your first three races."
                        : "Enforced by your rider category."}
                    </span>
                  </label>

                  <button
                    type="button"
                    className="ride-button ride-button--compact"
                    data-testid="register-race"
                    onClick={() => void register()}
                    disabled={!canRegister || registering}
                  >
                    {registering
                      ? "Registering…"
                      : full
                        ? "Race full"
                        : !open
                          ? race.phase === "scheduled"
                            ? "Registration opens soon"
                            : "Registration closed"
                          : "Register"}
                  </button>

                  <p className="register-note">
                    Registration closes two minutes before the start. A registered
                    rider enters the pen automatically.
                  </p>
                  {!open && me ? (
                    <p className="register-note">
                      Already registered? Start a free ride — the server moves
                      registered riders into the pen when it opens.
                    </p>
                  ) : null}
                </div>
              )}

              {actionError ? (
                <p className="form-error" role="alert">
                  {actionError}
                </p>
              ) : null}
            </div>

            <div className="race-detail__facts">
              <span>Mode · {race.mode}</span>
              <span>Server-authoritative</span>
              <span>Bots are separate from human standings</span>
            </div>
          </aside>
        </div>
      ) : null}
    </main>
  );
}
